/* eslint-disable jsx-a11y/click-events-have-key-events */
import { type DropdownMenu, IconDropdownMenu } from '@strata/react-dropdown-menu';
import { SuccessToast } from '@strata/react-toast-message';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps, useState } from 'react';
import { useNavigate } from 'react-router';

import { CaseAssignmentDialog } from './dialogs/CaseAssignmentDialog';
import { PauseIntakeCaseDialog } from './dialogs/PauseIntakeCaseDialog';

import { AttachDocumentsDialog } from 'components/intakeCases/dialogs/AttachDocumentsDialog';
import { ReopenIntakeCaseDialog } from 'components/intakeCases/dialogs/ReopenIntakeCaseDialog';
import { Permissions } from 'services/constants';
import { hasIntakeCaseBeenArchived } from 'services/helpers';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { type IntakeCase, usePostUnpauseIntakeCaseMutation } from 'store/api/intakeCaseApi';

interface IntakeCaseGridActionsMenuProps {
    intakeCase: Pick<
        IntakeCase,
        | 'intakeCaseId'
        | 'intakeStatus'
        | 'memberId'
        | 'memberFirstName'
        | 'memberLastName'
        | 'isBrand'
        | 'pausedAt'
        | 'assignedUsername'
        | 'memberEligible'
        | 'benefit'
        | 'productServiceOid'
        | 'diagnosisCode'
        | 'prescriberVerified'
    >;
}

export const IntakeCaseGridActionsMenu = ({ intakeCase }: IntakeCaseGridActionsMenuProps) => {
    const navigate = useNavigate();
    const { showIntakeCasePauseOption } = useFlags<{ showIntakeCasePauseOption: boolean }>();

    const [postUnpauseIntakeCase] = usePostUnpauseIntakeCaseMutation();

    const permissions = useAppSelector((state) => state.auth.permissions);
    const hasWriteIntakeCaseAdmin = permissions.includes(Permissions.WRITE_INTAKE_CASE_ADMIN);

    const [isAttachDocumentsDialogOpen, setIsAttachDocumentsDialogOpen] = useState(false);
    const [isReopenIntakeCaseDialogOpen, setIsReopenIntakeCaseDialogOpen] = useState(false);
    const [isPauseIntakeCaseDialogOpen, setIsPauseIntakeCaseDialogOpen] = useState(false);
    const [isCaseAssignmentDialogOpen, setIsCaseAssignmentDialogOpen] = useState(false);

    const isArchivedIntakeCase = hasIntakeCaseBeenArchived(intakeCase.intakeStatus);

    const menuItems: ComponentProps<typeof DropdownMenu>['menuItemArray'] = [
        {
            text: 'View Details',
            onItemClick: () => {
                navigate(AppRoutes.intakeCaseDetails(intakeCase.intakeCaseId));
            },
        },
    ];

    if (!isArchivedIntakeCase) {
        if (showIntakeCasePauseOption) {
            if (intakeCase.pausedAt) {
                menuItems.push({
                    text: 'Unsnooze Intake Case',
                    disabled: !hasWriteIntakeCaseAdmin,
                    onItemClick: () => {
                        void (async () => {
                            await postUnpauseIntakeCase({
                                intakeCaseId: intakeCase.intakeCaseId,
                            }).unwrap();
                            SuccessToast({ message: 'Intake case resumed successfully.' });
                        })();
                    },
                });
            } else {
                menuItems.push({
                    text: 'Snooze Intake Case',
                    disabled: !hasWriteIntakeCaseAdmin,
                    onItemClick: () => setIsPauseIntakeCaseDialogOpen(true),
                });
            }
        }
        menuItems.push({
            text: 'Select/Change Assignment',
            disabled: !permissions.includes(Permissions.WRITE_INTAKE_CASE),
            onItemClick: () => setIsCaseAssignmentDialogOpen(true),
        });
        menuItems.push({
            text: 'Attach Documents',
            disabled: !permissions.includes(Permissions.WRITE_INTAKE_CASE),
            onItemClick: () => setIsAttachDocumentsDialogOpen(true),
        });
    } else {
        menuItems.push({
            text: 'Reopen Intake Case',
            onItemClick: () => setIsReopenIntakeCaseDialogOpen(true),
        });
    }

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div onClick={(e) => e?.stopPropagation()}>
            <AttachDocumentsDialog
                isOpen={isAttachDocumentsDialogOpen}
                onClose={() => setIsAttachDocumentsDialogOpen(false)}
                intakeCase={intakeCase}
            />
            <ReopenIntakeCaseDialog
                isOpen={isReopenIntakeCaseDialogOpen}
                onClose={() => setIsReopenIntakeCaseDialogOpen(false)}
                intakeCase={intakeCase}
            />
            <PauseIntakeCaseDialog
                isOpen={isPauseIntakeCaseDialogOpen}
                onClose={() => setIsPauseIntakeCaseDialogOpen(false)}
                intakeCase={intakeCase}
            />
            <CaseAssignmentDialog
                isOpen={isCaseAssignmentDialogOpen}
                onClose={() => setIsCaseAssignmentDialogOpen(false)}
                intakeCase={intakeCase}
            />
            <IconDropdownMenu menuItems={menuItems} />
        </div>
    );
};
